import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { validateJob, validateDateParam } from '../lib/validation';
import { logJobCreated, logJobUpdated, logJobDeleted } from '../lib/auditLog';
import { escapeIlike } from '../lib/searchUtils';
import { getLocalDateString } from '../lib/dates';

function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);

  // Validate URL date param before using — guards against crafted URLs
  const rawDate = searchParams.get('date');
  const initialDate = rawDate && validateDateParam(rawDate) ? rawDate : getLocalDateString();

  const [propertySearch, setPropertySearch] = useState('');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    property_id: '',
    unit_number: '',
    job_type_text: '',
    scheduled_date: initialDate,
    urgency_flag: 'normal',
    deadline_date: '',
    special_notes: '',
  });

  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (isEditing) {
        const { data: job, error: jobError } = await supabase
          .from('jobs')
          .select('*, properties(id, name)')
          .eq('id', id)
          .eq('is_deleted', false)
          .single();

        if (!mounted) return;

        if (jobError || !job) {
          setError('Job not found');
          setLoading(false);
          return;
        }

        setFormData({
          property_id: job.property_id || '',
          unit_number: job.unit_number || '',
          job_type_text: job.job_type_text || '',
          scheduled_date: job.scheduled_date || '',
          urgency_flag: job.urgency_flag || 'normal',
          deadline_date: job.deadline_date || '',
          special_notes: job.special_notes || '',
        });

        if (job.properties) {
          setPropertySearch(job.properties.name);
          setSelectedPropertyName(job.properties.name);
        }

        const { data: jobTasks } = await supabase
          .from('job_tasks')
          .select('id, task_description, sort_order')
          .eq('job_id', id)
          .order('sort_order');

        if (!mounted) return;

        if (jobTasks) {
          setTasks(jobTasks.map((t) => ({ id: t.id, description: t.task_description })));
        }
      }

      if (mounted) setLoading(false);
    };

    fetchData();
    return () => { mounted = false; };
  }, [id, isEditing]);

  useEffect(() => {
    if (!propertySearch.trim()) {
      setPropertyOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('properties')
        .select('id, name')
        .eq('is_deleted', false)
        .ilike('name', `%${escapeIlike(propertySearch)}%`)
        .order('name')
        .limit(50);
      setPropertyOptions(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [propertySearch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTask = () => {
    setTasks((prev) => [...prev, { id: `new-${Date.now()}`, description: '' }]);
  };

  const handleTaskChange = (index, value) => {
    setTasks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], description: value };
      return updated;
    });
  };

  const handleRemoveTask = (index) => {
    setTasks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveTask = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === tasks.length - 1)
    ) {
      return;
    }

    setTasks((prev) => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[index + direction];
      updated[index + direction] = temp;
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate input
    const validation = validateJob(formData);
    if (!validation.isValid) {
      setError(Object.values(validation.errors)[0]);
      return;
    }

    setSaving(true);

    const jobData = {
      property_id: formData.property_id,
      unit_number: formData.unit_number || null,
      job_type_text: formData.job_type_text || null,
      scheduled_date: formData.scheduled_date,
      urgency_flag: formData.urgency_flag,
      deadline_date: formData.deadline_date || null,
      special_notes: formData.special_notes || null,
    };

    let jobId = id;

    if (isEditing) {
      const { error } = await supabase.from('jobs').update(jobData).eq('id', id);
      if (error) {
        setError(error.code === '23505' ? 'A job with these details already exists.' : 'Failed to update job');
        setSaving(false);
        return;
      }

      await supabase.from('job_tasks').delete().eq('job_id', id);

      logJobUpdated(id, {
        property: selectedPropertyName,
        scheduled_date: formData.scheduled_date,
        unit_number: formData.unit_number,
      });
    } else {
      // Get the next sort order for this day
      const { data: existingJobs } = await supabase
        .from('jobs')
        .select('daily_sort_order')
        .eq('scheduled_date', formData.scheduled_date)
        .eq('is_deleted', false)
        .order('daily_sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = existingJobs && existingJobs.length > 0 && existingJobs[0].daily_sort_order
        ? existingJobs[0].daily_sort_order + 1
        : 1;

      const { data, error } = await supabase
        .from('jobs')
        .insert([{ ...jobData, daily_sort_order: nextSortOrder }])
        .select('id')
        .single();

      if (error || !data) {
        setError(error?.code === '23505' ? 'A job with these details already exists.' : 'Failed to create job');
        setSaving(false);
        return;
      }
      jobId = data.id;

      logJobCreated(jobId, {
        property: selectedPropertyName,
        scheduled_date: formData.scheduled_date,
        unit_number: formData.unit_number,
      });
    }

    const validTasks = tasks.filter((t) => t.description.trim());
    if (validTasks.length > 0) {
      const taskInserts = validTasks.map((t, index) => ({
        job_id: jobId,
        task_description: t.description.trim(),
        sort_order: index,
      }));

      const { error: tasksError } = await supabase.from('job_tasks').insert(taskInserts);
      if (tasksError) {
        setError('Job saved but failed to save tasks');
        setSaving(false);
        return;
      }
    }

    navigate(`/schedule?date=${formData.scheduled_date}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    const { error } = await supabase
      .from('jobs')
      .update({ is_deleted: true })
      .eq('id', id);

    if (error) {
      setError('Failed to delete job');
    } else {
      // Clean up orphaned tasks so they don't linger in the DB
      await supabase.from('job_tasks').delete().eq('job_id', id);
      logJobDeleted(id, {
        property: selectedPropertyName,
        scheduled_date: formData.scheduled_date,
        unit_number: formData.unit_number,
      });
      navigate('/schedule');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <main className="max-w-3xl mx-auto py-6 px-4">
          <div className="text-center py-8 text-gray-500">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Job' : 'Add New Job'}
          </h1>
          {isEditing && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-red-600 hover:text-red-800 font-medium"
            >
              Delete Job
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={propertySearch}
                  onChange={(e) => {
                    setPropertySearch(e.target.value);
                    setShowPropertyDropdown(true);
                    if (!e.target.value) {
                      setFormData((prev) => ({ ...prev, property_id: '' }));
                      setSelectedPropertyName('');
                    }
                  }}
                  onFocus={() => { if (propertySearch) setShowPropertyDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowPropertyDropdown(false), 150)}
                  placeholder="Search properties..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {showPropertyDropdown && propertyOptions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                    {propertyOptions.map((p) => (
                      <li
                        key={p.id}
                        onMouseDown={() => {
                          setFormData((prev) => ({ ...prev, property_id: p.id }));
                          setPropertySearch(p.name);
                          setSelectedPropertyName(p.name);
                          setShowPropertyDropdown(false);
                        }}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {p.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Number
              </label>
              <input
                type="text"
                name="unit_number"
                value={formData.unit_number}
                onChange={handleInputChange}
                placeholder="e.g., 101, A-5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Type
              </label>
              <input
                type="text"
                name="job_type_text"
                value={formData.job_type_text}
                onChange={handleInputChange}
                maxLength={500}
                placeholder="e.g., Water Damage, Mold Remediation"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheduled Date *
              </label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Urgency
              </label>
              <select
                name="urgency_flag"
                value={formData.urgency_flag}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deadline Date
              </label>
              <input
                type="date"
                name="deadline_date"
                value={formData.deadline_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Special Notes
            </label>
            <textarea
              name="special_notes"
              value={formData.special_notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any special instructions for this job..."
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Task Checklist
              </label>
              <button
                type="button"
                onClick={handleAddTask}
                className="text-sm text-brand-coral hover:text-brand-coral-dark"
              >
                + Add Task
              </button>
            </div>

            {tasks.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No tasks added yet.</p>
            ) : (
              <div className="space-y-2">
                {tasks.map((task, index) => (
                  <div key={task.id} className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => handleMoveTask(index, -1)}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveTask(index, 1)}
                        disabled={index === tasks.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                      >
                        ▼
                      </button>
                    </div>
                    <input
                      type="text"
                      value={task.description}
                      onChange={(e) => handleTaskChange(index, e.target.value)}
                      placeholder="Task description"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveTask(index)}
                      className="text-red-500 hover:text-red-700 px-2"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-brand-coral text-white rounded-md hover:bg-brand-coral-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default JobForm;
